import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useQueryState, parseAsInteger, parseAsString } from "nuqs";
import { useDebounce } from "use-debounce";
import axiosInstance, { baseURL } from "../../../AxiosInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowsUpDown,
  faArrowUp,
  faFilter,
  faSearch,
  faXmark,
  faPause,
  faPlay,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

const Table = ({
  apiUrl,
  columns = [],
  itemsPerPageOptions = [10, 25, 50, 100],
  queryKey = "table-data",
  queryOptions = {},
  title = "Tableau de données",
  autoRefetchInterval = null, // Interval en millisecondes (ex: 30000 pour 30s)
  showAutoRefetchControls = false, // Afficher les contrôles d'auto-refetch
  refetchTime = null, // Temps de refetch en props (priorité sur autoRefetchInterval)
  refetchStartingField = null, // Fonction pour définir le champ de départ du refetch
  rowStyle = null, // Fonction pour personnaliser le style des lignes
  defaultFilters = {}, // Filtres par défaut à appliquer
}) => {
  // États URL avec nuqs
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState(
    "limit",
    parseAsInteger.withDefault(10)
  );
  const [ordering, setOrdering] = useQueryState(
    "ordering",
    parseAsString.withDefault("")
  );
  const [search, setSearch] = useQueryState(
    "search",
    parseAsString.withDefault("")
  );

  // États locaux pour les filtres par colonne
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilters, setShowFilters] = useState({});

  // États pour l'auto-refetch
  const [isAutoRefetchEnabled, setIsAutoRefetchEnabled] = useState(
    refetchTime ? true : false
  );
  const [autoRefetchIntervalState, setAutoRefetchIntervalState] = useState(
    refetchTime || autoRefetchInterval
  );
  const [lastRefetchStartingValue, setLastRefetchStartingValue] =
    useState(null);

  // Intervalles prédéfinis pour l'auto-refetch
  const autoRefetchIntervals = [
    { value: 5000, label: "5 secondes" },
    { value: 10000, label: "10 secondes" },
    { value: 30000, label: "30 secondes" },
    { value: 60000, label: "1 minute" },
    { value: 300000, label: "5 minutes" },
    { value: 600000, label: "10 minutes" },
  ];

  // Debounce pour les filtres par colonne (800ms)
  const [debouncedColumnFilters] = useDebounce(columnFilters, 800);
  // Debounce pour la recherche globale (500ms)
  const [debouncedSearch] = useDebounce(search, 500);

  // Construction des paramètres de requête
  const queryParams = useMemo(() => {
    const params = {
      limit,
      offset: (page - 1) * limit,
      page,
      ...(ordering && { ordering }),
      ...(debouncedSearch && { search: debouncedSearch }),
      ...debouncedColumnFilters,
      ...defaultFilters,
    };

    return params;
  }, [
    page,
    limit,
    ordering,
    debouncedSearch,
    debouncedColumnFilters,
    defaultFilters,
  ]);

  // Fonction pour fetcher les données
  const fetchTableData = async ({ queryKey }) => {
    const [_key, params] = queryKey;

    // Filtrer les paramètres vides
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ""
      )
    );

    // Ajouter le paramètre de départ si nécessaire pour l'auto-refetch
    if (
      isAutoRefetchEnabled &&
      refetchStartingField &&
      lastRefetchStartingValue
    ) {
      const startingField = refetchStartingField(data?.results || []);
      if (startingField && startingField.key && startingField.value) {
        cleanParams[`starting_${startingField.key}`] = startingField.value;
      }
    } else if (isAutoRefetchEnabled && !refetchStartingField) {
      // Si pas de refetchStartingField, faire un refetch de la page actuelle
      // Ne pas forcer page=1 et offset=0, laisser les paramètres actuels
    }

    const queryString = new URLSearchParams(cleanParams).toString();

    const response = await axiosInstance.get(`${apiUrl}/?${queryString}`);

    if (!response.data) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    // Mettre à jour la valeur de départ pour le prochain refetch seulement si refetchStartingField est défini
    if (
      isAutoRefetchEnabled &&
      refetchStartingField &&
      response.data.results &&
      response.data.results.length > 0
    ) {
      const startingField = refetchStartingField(response.data.results);
      if (startingField && startingField.key && startingField.value) {
        setLastRefetchStartingValue(startingField.value);
      }
    }

    return response.data;
  };

  // Query TanStack
  const {
    data = { count: 0, results: [], next: null, previous: null },
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: refetchStartingField
      ? [queryKey, queryParams, lastRefetchStartingValue]
      : [queryKey, queryParams],
    queryFn: fetchTableData,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval:
      isAutoRefetchEnabled && autoRefetchIntervalState
        ? autoRefetchIntervalState
        : false,
    refetchIntervalInBackground: false, // Ne pas refetch en arrière-plan
    ...queryOptions,
  });

  // Fonction helper pour obtenir le nom lisible d'une colonne
  const getColumnTitle = (field) => {
    const column = columns.find(
      (col) => (col.sortField || col.internal) === field
    );
    return column ? column.title : field;
  };

  // Fonction helper pour obtenir l'icône de direction du tri
  const getDirectionIcon = (order) => {
    const isDescending = order.startsWith("-");
    return isDescending ? faArrowDown : faArrowUp;
  };

  // Fonction helper pour accéder aux propriétés imbriquées (ex: "user.name", "user.test.id")
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;

    // Si le chemin contient des points, on navigue dans l'objet
    if (path.includes(".")) {
      return path.split(".").reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);
    }

    // Sinon, accès direct à la propriété
    return obj[path];
  };

  // Gestion du tri multiple
  const handleSort = (field) => {
    if (!field) return;

    // Parser les tris actuels
    const currentOrderings = ordering
      ? ordering.split(",").map((o) => o.trim())
      : [];

    // Trouver l'index du champ dans les tris actuels
    const fieldIndex = currentOrderings.findIndex(
      (o) => o === field || o === `-${field}`
    );

    let newOrderings;

    if (fieldIndex === -1) {
      // Le champ n'est pas encore trié, l'ajouter en premier
      newOrderings = [field, ...currentOrderings];
    } else {
      const currentOrder = currentOrderings[fieldIndex];

      if (currentOrder === field) {
        // Passer de ascendant à descendant
        newOrderings = [...currentOrderings];
        newOrderings[fieldIndex] = `-${field}`;
      } else if (currentOrder === `-${field}`) {
        // Supprimer le tri pour ce champ
        newOrderings = currentOrderings.filter(
          (_, index) => index !== fieldIndex
        );
      }
    }

    // Limiter à 3 tris maximum pour éviter la complexité
    if (newOrderings.length > 3) {
      newOrderings = newOrderings.slice(0, 3);
    }

    const newOrdering = newOrderings.join(",");
    setOrdering(newOrdering);
    setPage(1); // Reset à la première page
  };

  // Gestion des filtres par colonne avec debounce
  const handleColumnFilter = useCallback(
    (field, value) => {
      const newFilters = { ...columnFilters };
      if (value && value.trim()) {
        newFilters[field] = value;
      } else {
        delete newFilters[field];
      }
      setColumnFilters(newFilters);
      setPage(1); // Reset à la première page
    },
    [columnFilters, setPage]
  );

  // Toggle affichage du filtre
  const toggleFilter = (field) => {
    setShowFilters((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Fonction pour ouvrir le filtre et focus l'input
  const openFilterAndFocus = (field) => {
    // Si le filtre est déjà ouvert, on le ferme
    if (showFilters[field]) {
      setShowFilters((prev) => ({
        ...prev,
        [field]: false,
      }));
      return;
    }

    // Ouvrir le filtre sans fermer les autres
    setShowFilters((prev) => ({
      ...prev,
      [field]: true,
    }));

    // Focus l'input après un petit délai pour que le DOM soit mis à jour
    setTimeout(() => {
      const filterInput = document.querySelector(
        `input[data-filter-field="${field}"]`
      );
      if (filterInput) {
        filterInput.focus();
        // Optionnel : sélectionner le texte existant
        filterInput.select();
      }
    }, 150); // Augmenté le délai pour s'assurer que le DOM est mis à jour
  };

  // Fonction pour fermer tous les filtres
  const closeAllFilters = () => {
    setShowFilters({});
  };

  // Fonctions pour l'auto-refetch
  const toggleAutoRefetch = () => {
    const newState = !isAutoRefetchEnabled;
    setIsAutoRefetchEnabled(newState);

    // Réinitialiser la valeur de départ si on désactive l'auto-refetch
    if (!newState) {
      setLastRefetchStartingValue(null);
    } else if (
      refetchStartingField &&
      data?.results &&
      data.results.length > 0
    ) {
      // Initialiser la valeur de départ avec le premier élément de la première page
      const startingField = refetchStartingField(data.results);
      if (startingField && startingField.key && startingField.value) {
        setLastRefetchStartingValue(startingField.value);
      }
    }
  };

  const updateAutoRefetchInterval = (newInterval) => {
    setAutoRefetchIntervalState(newInterval);
  };

  const resetAutoRefetch = () => {
    setLastRefetchStartingValue(null);
    if (
      isAutoRefetchEnabled &&
      refetchStartingField &&
      data?.results &&
      data.results.length > 0
    ) {
      // Réinitialiser avec le premier élément actuel
      const startingField = refetchStartingField(data.results);
      if (startingField && startingField.key && startingField.value) {
        setLastRefetchStartingValue(startingField.value);
      }
    }
    if (isAutoRefetchEnabled) {
      refetch(); // Forcer un refetch immédiat
    }
  };

  const formatInterval = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${Math.floor(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}min`;
    return `${Math.floor(ms / 3600000)}h`;
  };

  const getNextRefetchTime = () => {
    if (!isAutoRefetchEnabled || !autoRefetchIntervalState) return null;
    return formatInterval(autoRefetchIntervalState);
  };

  const getAutoRefetchStatus = () => {
    if (!isAutoRefetchEnabled) return "Auto-refresh désactivé";

    let status = `Auto-refresh: ${getNextRefetchTime()}`;
    if (refetchStartingField && lastRefetchStartingValue) {
      const startingField = refetchStartingField(data?.results || []);
      if (startingField && startingField.key) {
        status += ` (depuis ${startingField.key}: ${lastRefetchStartingValue})`;
      }
    } else if (!refetchStartingField) {
      status += " (refetch complet)";
    }
    return status;
  };

  // Gestionnaire pour fermer les filtres quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isFilterClick =
        event.target.closest(".dynamic-table-filter") ||
        event.target.closest(".dynamic-table-filter-btn") ||
        event.target.closest(".dynamic-table-column-title");

      if (!isFilterClick) {
        // Fermer tous les filtres quand on clique en dehors
        closeAllFilters();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Initialiser la valeur de départ quand les données sont chargées
  useEffect(() => {
    if (
      isAutoRefetchEnabled &&
      refetchStartingField &&
      data?.results &&
      data.results.length > 0 &&
      !lastRefetchStartingValue
    ) {
      const startingField = refetchStartingField(data.results);
      if (startingField && startingField.key && startingField.value) {
        setLastRefetchStartingValue(startingField.value);
      }
    }
  }, [
    isAutoRefetchEnabled,
    refetchStartingField,
    data?.results,
    lastRefetchStartingValue,
  ]);

  // Activer automatiquement l'auto-refetch quand refetchTime est fourni
  useEffect(() => {
    if (refetchTime && !isAutoRefetchEnabled) {
      setIsAutoRefetchEnabled(true);
      setAutoRefetchIntervalState(refetchTime);
    }
  }, [refetchTime, isAutoRefetchEnabled]);

  // Icône de tri avec support pour les tris multiples
  const getSortIcon = (field) => {
    if (!ordering) {
      return (
        <FontAwesomeIcon
          icon={faArrowsUpDown}
          size="lg"
          style={{ opacity: 0.5 }}
        />
      );
    }

    const orderings = ordering.split(",").map((o) => o.trim());
    const fieldIndex = orderings.findIndex(
      (o) => o === field || o === `-${field}`
    );

    if (fieldIndex === -1) {
      return (
        <FontAwesomeIcon
          icon={faArrowsUpDown}
          size="lg"
          style={{ opacity: 0.5 }}
        />
      );
    }

    const order = orderings[fieldIndex];
    const isAscending = order === field;
    const isDescending = order === `-${field}`;

    // Afficher l'ordre du tri (1, 2, 3) pour les tris multiples
    const orderNumber = fieldIndex + 1;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
        {isAscending && <FontAwesomeIcon icon={faArrowUp} size="lg" />}
        {isDescending && <FontAwesomeIcon icon={faArrowDown} size="lg" />}
        {orderings.length > 1 && (
          <span
            style={{
              fontSize: "10px",
              fontWeight: "bold",
              color: "#64748b",
              backgroundColor: "#f1f5f9",
              borderRadius: "50%",
              width: "16px",
              height: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            {orderNumber}
          </span>
        )}
      </div>
    );
  };

  // Calculs pagination
  const totalItems = data.count || 0;
  const totalPages = Math.ceil(totalItems / limit);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, totalItems);

  // Navigation pagination
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const getPaginationNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  // Calcul du nombre de filtres actifs
  const activeFiltersCount = Object.values(showFilters).filter(Boolean).length;
  const activeColumnFiltersCount = Object.keys(columnFilters).length;

  // Styles CSS
  const styles = {
    container: {
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #f1f5f9",
      overflow: "hidden",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      padding: "1.5rem",
      borderBottom: "1px solid #f1f5f9",
      backgroundColor: "#f8fafc",
    },
    headerContent: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "1rem",
    },
    title: {
      fontSize: "1.5rem",
      fontWeight: "600",
      color: "#1e293b",
      margin: "0",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
    },
    titleIcon: {
      fontSize: "1.25rem",
      color: "#64748b",
    },
    headerControls: {
      display: "flex",
      alignItems: "center",
      gap: "1rem",
      flexWrap: "wrap",
    },
    pageInfo: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: "0.5rem 1rem",
      backgroundColor: "white",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      fontSize: "14px",
      color: "#64748b",
      fontWeight: "500",
    },
    pageCount: {
      color: "#1e293b",
      fontWeight: "600",
    },
    refreshButton: {
      padding: "0.5rem 1rem",
      fontSize: "14px",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      backgroundColor: "white",
      color: "#64748b",
      cursor: "pointer",
      transition: "all 0.2s ease",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    thead: {
      backgroundColor: "#f8fafc",
    },
    th: {
      padding: "1rem 1.5rem",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: "600",
      color: "#475569",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: "1px solid #e2e8f0",
      backgroundColor: "#f8fafc",
    },
    thContent: {
      display: "flex",
      flexDirection: "column",
      gap: "0.5rem",
    },
    thRow: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    sortButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "#64748b",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      borderRadius: "4px",
      transition: "all 0.2s ease",
    },
    filterButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "#64748b",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      borderRadius: "4px",
      transition: "all 0.2s ease",
    },
    filterInput: {
      width: "100%",
      padding: "0.5rem 0.75rem",
      fontSize: "12px",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      outline: "none",
      backgroundColor: "white",
      transition: "all 0.2s ease",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    },
    tbody: {
      backgroundColor: "white",
    },
    tr: {
      borderBottom: "1px solid #f1f5f9",
      transition: "all 0.2s ease",
    },
    td: {
      padding: "1rem 1.5rem",
      fontSize: "14px",
      color: "#1e293b",
      textAlign: "left",
      lineHeight: "1.5",
    },
    loadingContainer: {
      padding: "3rem",
      textAlign: "center",
    },
    loadingSpinner: {
      display: "inline-block",
      width: "24px",
      height: "24px",
      border: "2px solid #f1f5f9",
      borderTop: "2px solid #3b82f6",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      marginRight: "0.75rem",
    },
    emptyState: {
      padding: "3rem",
      textAlign: "center",
      color: "#64748b",
    },
    errorContainer: {
      padding: "3rem",
      textAlign: "center",
    },
    errorIcon: {
      width: "48px",
      height: "48px",
      color: "#ef4444",
      margin: "0 auto 1rem",
    },
    pagination: {
      padding: "1.5rem",
      borderTop: "1px solid #f1f5f9",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "1rem",
      backgroundColor: "#f8fafc",
    },
    paginationInfo: {
      fontSize: "14px",
      color: "#64748b",
      fontWeight: "500",
    },
    paginationControls: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    pageButton: {
      padding: "0.5rem 0.75rem",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      backgroundColor: "white",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "36px",
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.2s ease",
      color: "#475569",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    },
    pageButtonActive: {
      backgroundColor: "#3b82f6",
      color: "white",
      borderColor: "#3b82f6",
      boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.2)",
    },
    pageButtonDisabled: {
      opacity: "0.5",
      cursor: "not-allowed",
      backgroundColor: "#f8fafc",
    },
    select: {
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      padding: "0.5rem 0.75rem",
      fontSize: "14px",
      outline: "none",
      backgroundColor: "white",
      transition: "all 0.2s ease",
      color: "#64748b",
    },
    dynamicTableFilter: {
      transition: "all 0.2s ease",
    },
    dynamicTableFilterFocus: {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.1)",
      transform: "translateY(-1px)",
    },
    dynamicTableSelectFocus: {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.1)",
      transform: "translateY(-1px)",
    },
    autoRefetchContainer: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: "0.5rem 1rem",
      backgroundColor: isAutoRefetchEnabled ? "#dbeafe" : "#f8fafc",
      borderRadius: "8px",
      border: `1px solid ${isAutoRefetchEnabled ? "#93c5fd" : "#e2e8f0"}`,
      transition: "all 0.2s ease",
    },
    autoRefetchButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "0.25rem",
      borderRadius: "4px",
      color: isAutoRefetchEnabled ? "#1e40af" : "#64748b",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
    },
    autoRefetchIntervalSelect: {
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      padding: "0.25rem 0.5rem",
      fontSize: "12px",
      outline: "none",
      backgroundColor: "white",
      color: "#64748b",
      minWidth: "80px",
    },
    autoRefetchStatus: {
      fontSize: "12px",
      color: isAutoRefetchEnabled ? "#1e40af" : "#64748b",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "0.25rem",
    },
  };

  // Gestion des erreurs
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "500",
              color: "#111827",
              marginBottom: "0.5rem",
            }}
          >
            Erreur de chargement
          </h3>
          <p style={{ color: "#6b7280", marginBottom: "1rem" }}>
            {error.message}
          </p>
          <button
            onClick={() => refetch()}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .dynamic-table-row:hover {
            background-color: #f8fafc;
            transform: translateY(-1px);
            box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
          }

          .dynamic-table-filter {
            transition: all 0.2s ease;
          }

          .dynamic-table-filter:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
            transform: translateY(-1px);
          }

          .dynamic-table-select:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
            transform: translateY(-1px);
          }

          .dynamic-table-refresh:hover {
            background-color: #f1f5f9;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }

          .dynamic-table-sort:hover {
            color: #1e293b;
            background-color: #f1f5f9;
            transform: scale(1.1);
          }

          .dynamic-table-filter-btn:hover {
            color: #1e293b;
            background-color: #f1f5f9;
            transform: scale(1.1);
          }

          .dynamic-table-column-title:hover {
            color: #3b82f6;
          }

          .dynamic-table-page:hover:not(:disabled) {
            background-color: #f1f5f9;
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }

          .dynamic-table-container {
            transition: all 0.3s ease;
          }

          .dynamic-table-container:hover {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }

          @media (min-width: 640px) {
            .dynamic-table-pagination {
              flex-direction: row;
            }
          }

          /* Amélioration de l'accessibilité */
          .dynamic-table-sort:focus,
          .dynamic-table-filter-btn:focus,
          .dynamic-table-page:focus {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }

          /* Animation d'entrée pour les lignes */
          .dynamic-table-row {
            animation: fadeInUp 0.3s ease-out;
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
              max-height: 0;
            }
            to {
              opacity: 1;
              transform: translateY(0);
              max-height: 100px;
            }
          }

          /* Animation pour l'auto-refetch */
          .dynamic-table-auto-refetch-active {
            animation: pulse 2s infinite;
          }

          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
            }
            70% {
              box-shadow: 0 0 0 6px rgba(59, 130, 246, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
            }
          }

          .dynamic-table-auto-refetch-button:hover {
            transform: scale(1.1);
            background-color: #f1f5f9;
          }

          .dynamic-table-auto-refetch-container:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
        `}
      </style>

      <div style={styles.container} className="dynamic-table-container">
        {/* Header simple avec titre et contrôles */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <h1 style={styles.title}>
              {/* <FontAwesomeIcon icon={faSearch} style={styles.titleIcon} /> */}
              {title}
            </h1>

            <div style={styles.headerControls}>
              <div style={styles.pageInfo}>
                <span>Page</span>
                <span style={styles.pageCount}>
                  {page} / {totalPages}
                </span>
              </div>

              {activeColumnFiltersCount > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 1rem",
                    backgroundColor: "#dbeafe",
                    borderRadius: "8px",
                    border: "1px solid #93c5fd",
                    fontSize: "14px",
                    color: "#1e40af",
                    fontWeight: "500",
                  }}
                >
                  <FontAwesomeIcon icon={faFilter} />
                  <span>
                    {activeColumnFiltersCount} filtre
                    {activeColumnFiltersCount > 1 ? "s" : ""} actif
                    {activeColumnFiltersCount > 1 ? "s" : ""}
                  </span>
                </div>
              )}

              {/* Indicateur des tris actifs */}
              {ordering && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 1rem",
                    backgroundColor: "#fef3c7",
                    borderRadius: "8px",
                    border: "1px solid #f59e0b",
                    fontSize: "14px",
                    color: "#92400e",
                    fontWeight: "500",
                  }}
                >
                  <FontAwesomeIcon icon={faArrowsUpDown} />
                  <span>Tris actifs:</span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      flexWrap: "wrap",
                    }}
                  >
                    {ordering.split(",").map((order, index) => {
                      const field = order.replace(/^-/, "");
                      const isDescending = order.startsWith("-");
                      const columnTitle = getColumnTitle(field);

                      return (
                        <span
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            padding: "0.25rem 0.5rem",
                            backgroundColor: "white",
                            borderRadius: "4px",
                            fontSize: "12px",
                            cursor: "pointer",
                            border: "1px solid #f59e0b",
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => {
                            // Supprimer ce tri spécifique
                            const newOrderings = ordering
                              .split(",")
                              .filter((_, i) => i !== index);
                            setOrdering(newOrderings.join(","));
                            setPage(1);
                          }}
                          title={`Cliquer pour supprimer le tri ${index + 1}`}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#fef3c7";
                            e.target.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "white";
                            e.target.style.transform = "scale(1)";
                          }}
                        >
                          <span
                            style={{ fontWeight: "bold", color: "#92400e" }}
                          >
                            {index + 1}
                          </span>
                          <span>{columnTitle}</span>
                          <FontAwesomeIcon
                            icon={getDirectionIcon(order)}
                            size="xs"
                            style={{ color: "#92400e" }}
                          />
                          <FontAwesomeIcon
                            icon={faXmark}
                            size="xs"
                            style={{
                              color: "#92400e",
                              marginLeft: "2px",
                              opacity: 0.7,
                            }}
                          />
                        </span>
                      );
                    })}
                    <button
                      onClick={() => {
                        setOrdering("");
                        setPage(1);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        padding: "0.25rem 0.5rem",
                        backgroundColor: "#fee2e2",
                        border: "1px solid #ef4444",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: "pointer",
                        color: "#991b1b",
                        fontWeight: "500",
                        transition: "all 0.2s ease",
                      }}
                      title="Effacer tous les tris"
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#fecaca";
                        e.target.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#fee2e2";
                        e.target.style.transform = "scale(1)";
                      }}
                    >
                      <FontAwesomeIcon icon={faXmark} size="xs" />
                      Effacer tout
                    </button>
                  </div>
                </div>
              )}

              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <span style={{ fontSize: "14px", color: "#64748b" }}>
                  Afficher:
                </span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value));
                    setPage(1);
                  }}
                  style={styles.select}
                  className="dynamic-table-select"
                >
                  {itemsPerPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => refetch()}
                disabled={isLoading}
                style={{
                  ...styles.refreshButton,
                  ...(isLoading && { opacity: 0.5, cursor: "not-allowed" }),
                }}
                className="dynamic-table-refresh"
              >
                <FontAwesomeIcon icon={faSearch} />
                {isLoading ? "Actualisation..." : "Actualiser"}
              </button>

              {/* Contrôles d'auto-refetch */}
              {showAutoRefetchControls && (
                <div
                  style={styles.autoRefetchContainer}
                  className={`dynamic-table-auto-refetch-container ${
                    isAutoRefetchEnabled
                      ? "dynamic-table-auto-refetch-active"
                      : ""
                  }`}
                >
                  <button
                    onClick={toggleAutoRefetch}
                    style={styles.autoRefetchButton}
                    className="dynamic-table-auto-refetch-button"
                    title={
                      isAutoRefetchEnabled
                        ? "Arrêter l'auto-refresh"
                        : "Démarrer l'auto-refresh"
                    }
                  >
                    <FontAwesomeIcon
                      icon={isAutoRefetchEnabled ? faPause : faPlay}
                      size="sm"
                    />
                  </button>

                  <select
                    value={autoRefetchIntervalState || ""}
                    onChange={(e) =>
                      updateAutoRefetchInterval(
                        parseInt(e.target.value) || null
                      )
                    }
                    style={styles.autoRefetchIntervalSelect}
                    className="dynamic-table-select"
                    disabled={isAutoRefetchEnabled}
                  >
                    <option value="">Désactivé</option>
                    {autoRefetchIntervals.map((interval) => (
                      <option key={interval.value} value={interval.value}>
                        {interval.label}
                      </option>
                    ))}
                  </select>

                  <div style={styles.autoRefetchStatus}>
                    <FontAwesomeIcon icon={faClock} size="xs" />
                    {getAutoRefetchStatus()}
                  </div>

                  {/* Bouton de reset pour l'auto-refetch */}
                  {isAutoRefetchEnabled &&
                    refetchStartingField &&
                    lastRefetchStartingValue && (
                      <button
                        onClick={resetAutoRefetch}
                        style={{
                          ...styles.autoRefetchButton,
                          fontSize: "10px",
                          padding: "0.25rem 0.5rem",
                          backgroundColor: "#fee2e2",
                          color: "#991b1b",
                          border: "1px solid #ef4444",
                          borderRadius: "4px",
                        }}
                        title="Réinitialiser l'auto-refresh"
                      >
                        Reset
                      </button>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                {columns.map((column) => (
                  <th key={column.internal} style={styles.th}>
                    <div style={styles.thContent}>
                      <div style={styles.thRow}>
                        <span
                          style={{
                            cursor: column.filterable ? "pointer" : "default",
                            transition: "color 0.2s ease",
                          }}
                          onClick={() =>
                            column.filterable &&
                            openFilterAndFocus(column.internal)
                          }
                          className={
                            column.filterable
                              ? "dynamic-table-column-title"
                              : ""
                          }
                        >
                          {column.title}
                        </span>
                        {column.sortable && (
                          <button
                            onClick={() =>
                              handleSort(column.sortField || column.internal)
                            }
                            style={styles.sortButton}
                            className="dynamic-table-sort"
                          >
                            {getSortIcon(column.sortField || column.internal)}
                          </button>
                        )}
                        {column.filterable && (
                          <button
                            onClick={() => openFilterAndFocus(column.internal)}
                            style={styles.filterButton}
                            className="dynamic-table-filter-btn"
                          >
                            <FontAwesomeIcon icon={faFilter} />
                          </button>
                        )}
                      </div>

                      {/* Filtre par colonne */}
                      {column.filterable && showFilters[column.internal] && (
                        <div
                          style={{
                            animation: "slideDown 0.2s ease-out",
                            overflow: "hidden",
                          }}
                        >
                          {column.dateFilter ? (
                            // Filtre de date avec début et fin
                            <div
                              style={{
                                display: "flex",
                                gap: "0.5rem",
                                flexDirection: "column",
                              }}
                            >
                              <input
                                type="date"
                                placeholder={`Date début ${column.title.toLowerCase()}...`}
                                value={
                                  columnFilters[
                                    column.searchField || column.internal
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleColumnFilter(
                                    column.searchField || column.internal,
                                    e.target.value
                                  )
                                }
                                style={styles.filterInput}
                                className="dynamic-table-filter"
                                onClick={(e) => e.stopPropagation()}
                                data-filter-field={`${column.internal}_start`}
                              />
                              <input
                                type="date"
                                placeholder={`Date fin ${column.title.toLowerCase()}...`}
                                value={
                                  columnFilters[
                                    `${
                                      column.searchField || column.internal
                                    }_end`
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleColumnFilter(
                                    `${
                                      column.searchField || column.internal
                                    }_end`,
                                    e.target.value
                                  )
                                }
                                style={styles.filterInput}
                                className="dynamic-table-filter"
                                onClick={(e) => e.stopPropagation()}
                                data-filter-field={`${column.internal}_end`}
                              />
                              {(columnFilters[
                                column.searchField || column.internal
                              ] ||
                                columnFilters[
                                  `${column.searchField || column.internal}_end`
                                ]) && (
                                <div
                                  style={{
                                    fontSize: "10px",
                                    color: "#64748b",
                                    marginTop: "2px",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {debouncedColumnFilters[
                                    column.searchField || column.internal
                                  ] !==
                                    columnFilters[
                                      column.searchField || column.internal
                                    ] ||
                                  debouncedColumnFilters[
                                    `${
                                      column.searchField || column.internal
                                    }_end`
                                  ] !==
                                    columnFilters[
                                      `${
                                        column.searchField || column.internal
                                      }_end`
                                    ]
                                    ? "Filtrage..."
                                    : "Filtré"}
                                </div>
                              )}
                            </div>
                          ) : (
                            // Filtre texte normal
                            <>
                              <input
                                type="text"
                                placeholder={`Filtrer ${column.title.toLowerCase()}...`}
                                value={
                                  columnFilters[
                                    column.searchField || column.internal
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleColumnFilter(
                                    column.searchField || column.internal,
                                    e.target.value
                                  )
                                }
                                style={styles.filterInput}
                                className="dynamic-table-filter"
                                onClick={(e) => e.stopPropagation()}
                                data-filter-field={column.internal}
                              />
                              {columnFilters[
                                column.searchField || column.internal
                              ] && (
                                <div
                                  style={{
                                    fontSize: "10px",
                                    color: "#64748b",
                                    marginTop: "2px",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {debouncedColumnFilters[
                                    column.searchField || column.internal
                                  ] !==
                                  columnFilters[
                                    column.searchField || column.internal
                                  ]
                                    ? "Filtrage..."
                                    : "Filtré"}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody style={styles.tbody}>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} style={styles.loadingContainer}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <div style={styles.loadingSpinner}></div>
                      <span style={{ color: "#6b7280" }}>Chargement...</span>
                    </div>
                  </td>
                </tr>
              ) : data.results && data.results.length > 0 ? (
                data.results.map((item, index) => (
                  <tr
                    key={item.id || index}
                    style={{
                      ...styles.tr,
                      ...(rowStyle ? rowStyle(item, index) : {}),
                    }}
                    className="dynamic-table-row"
                  >
                    {columns.map((column) => (
                      <td key={column.internal} style={styles.td}>
                        {column?.code
                          ? column.code(
                              item,
                              getNestedValue(item, column.internal)
                            )
                          : getNestedValue(item, column.internal) || "-"}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} style={styles.emptyState}>
                    Aucune donnée disponible
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div style={styles.pagination} className="dynamic-table-pagination">
            <div style={styles.paginationInfo}>
              Affichage de {startItem} à {endItem} sur {totalItems} résultats
            </div>

            <div style={styles.paginationControls}>
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || isLoading}
                style={{
                  ...styles.pageButton,
                  ...(page <= 1 || isLoading ? styles.pageButtonDisabled : {}),
                }}
                className="dynamic-table-page"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                {getPaginationNumbers().map((pageNum, index) => (
                  <React.Fragment key={index}>
                    {pageNum === "..." ? (
                      <span
                        style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        onClick={() => goToPage(pageNum)}
                        disabled={isLoading}
                        style={{
                          ...styles.pageButton,
                          ...(page === pageNum ? styles.pageButtonActive : {}),
                          ...(isLoading ? styles.pageButtonDisabled : {}),
                        }}
                        className="dynamic-table-page"
                      >
                        {pageNum}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </div>

              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || isLoading}
                style={{
                  ...styles.pageButton,
                  ...(page >= totalPages || isLoading
                    ? styles.pageButtonDisabled
                    : {}),
                }}
                className="dynamic-table-page"
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Table;
